import React, { useEffect, useState } from 'react';
import startOfTomorrow from 'date-fns/startOfTomorrow';
import { Box, FlexBox, FormDialog, Grid, MuiTable, SvgIcon, Switch, Typography } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as kiosksActions, selectors as kiosksSelectors } from 'store/kiosks';
import { KioskHardware } from 'store/kioskHardware';
import { selectors as authSelectors } from 'store/auth';
import { orDash, formatHardwareTitle } from 'utils/stringFormat';
import { useSelector } from 'react-redux';
import { useStyles } from '../styled';
import { BaseOverlayProps } from '../types';

const StatusOverlay = (props: BaseOverlayProps) => {
  const { isOpen, onClose, kiosk } = props;

  const { updateKiosk } = useActions({
    updateKiosk: kiosksActions.updateKiosk
  });

  const kioskData = useSelector(kiosksSelectors.selectKiosk);
  const admin = useSelector(authSelectors.selectAdmin);

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [kioskStatus, setKioskStatus] = useState<string>(kiosk.status);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(false);

  const classes = useStyles({ showOverlay: isOpen })();

  // check for user is Read-Only Admin or not, if yes hide switch button and show text
  useEffect(() => {
    if (admin && admin.role === 'ReadOnlyAdmin') setReadOnlyAdmin(true);
  }, [admin]);

  const { changeStatusKiosk } = useActions({ changeStatusKiosk: kiosksActions.changeStatusKiosk });

  const onSwitchStatus = (checked: boolean) => {
    setShowConfirmation(true);

    const status = checked ? 'Active' : 'Inactive';
    // TODO: log event
    changeStatusKiosk({ kioskId: kiosk.id, status, adminId: admin ? admin.id : '' });
    setKioskStatus(status);

    updateKiosk({
      id: kiosk.id,
      status,
      unscheduledUntil: startOfTomorrow()
    });
  };

  const HardwareStatus = (Status: { type: string }) => {
    const status = Object.values(Status).toString();
    if (status === 'Inactive') {
      return <SvgIcon name="error_circle" color="#C40B0F" />;
    } else if (status === 'Active') {
      return <SvgIcon name="success_circle" color="#64AF15" />;
    }
    return <Typography>--</Typography>;
  };

  const closeConfirmOverlay = () => {
    setShowConfirmation(false);
  };

  const getHardwareDisplayName = (kioskHardware: KioskHardware) => {
    const { type, configuration } = kioskHardware;

    if (type === 'printer' && configuration.displayName) {
      return `${configuration.displayName} ${formatHardwareTitle(type)}`;
    }

    return formatHardwareTitle(type);
  };

  return (
    <>
      {/* INFO OVERLAY */}
      {showConfirmation && (
        <FormDialog
          cancelButtonAction={closeConfirmOverlay}
          confirmButtonAction={closeConfirmOverlay}
          confirmButtonText="Ok"
          open={showConfirmation}
        >
          <Typography fontSize={24} maxWidth="524px">
            {kiosk.name} will remain {kioskStatus} until it resumes its scheduled operation tomorrow.
          </Typography>
        </FormDialog>
      )}

      <FormDialog
        cancelButtonAction={onClose}
        confirmButtonAction={onClose}
        confirmButtonText="Ok"
        open={isOpen}
        title="Kiosk Status"
      >
        <Grid direction="column">
          {/* KIOSK STATUS SWITCH */}
          <FlexBox className={classes.statusOverlaySwitchBox}>
            <Typography fontSize="16px" fontWeight={600}>
              Status
            </Typography>
            <Switch
              checked={kiosk.status === 'Active'}
              onChange={(_e, checked) => onSwitchStatus(checked)}
              data-testid="kiosk-status-switch"
              checkedLabel="ACTIVE"
              uncheckedLabel="INACTIVE"
              disabled={!readOnlyAdmin ? false : true}
            />
          </FlexBox>
          {/* KIOSK HARDWARE TABLE */}
          <Box className={classes.statusOverlayTableBox}>
            <MuiTable.TableContainer id="kioskHardwareTable">
              <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
                <MuiTable.TableHead sx={{ background: 'white' }}>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell className={classes.kioskHardwareHeaderType} sx={{ width: '250px' }}>
                      Type
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.kioskHardwareHeaders} sx={{ width: '250px' }}>
                      Serial Number
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.kioskHardwareHeaders} sx={{ width: '80px' }}>
                      Status
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {((kioskData && kioskData.hardware) || []).map((kioskHardwareObj: KioskHardware) => {
                    return (
                      <MuiTable.TableRow className={classes.kioskHardwareTableRows}>
                        <MuiTable.TableCell className={classes.kioskHardwareType}>
                          <Typography fontSize={16} fontWeight={600}>
                            {getHardwareDisplayName(kioskHardwareObj)}
                          </Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography fontSize={16}>{orDash(kioskHardwareObj.serialCode)}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <HardwareStatus type={kioskHardwareObj.status} />
                        </MuiTable.TableCell>
                      </MuiTable.TableRow>
                    );
                  })}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Grid>
      </FormDialog>
    </>
  );
};

export default StatusOverlay;
